import { api } from "../net/api.js";
import type { Wallet } from "./beans.js";
import { saveWallet, syncBeans } from "./beans.js";
import type { Feat, FeatContext, FeatPlay } from "./feats.js";
import { joinFeats, loadFeats, recordFeats, saveFeats } from "./feats.js";
import type { Achievement } from "@obean/game-contract";

/**
 * The signed-in person on this page, or null for the browser's guest (ADR 0029): asked once, so
 * beans and progress follow the same person. Offline or no guest yet: the guest. `ownBrowser`: the
 * browser is the person's own (their first), so the guest's progress and beans are theirs too.
 */
let asked: Promise<{ id: string; ownBrowser: boolean } | null> | null = null;
function signedIn() {
  asked ??= api.me().then(
    (me) =>
      me.signedIn === true && me.personId
        ? { id: me.personId, ownBrowser: me.ownBrowser === true }
        : null,
    () => null,
  );
  return asked;
}

export async function profileId(): Promise<string | null> {
  return (await signedIn())?.id ?? null;
}

/**
 * A wallet after a play (ADR 0043): kept in this browser, and for a signed-in person synced with
 * their other devices (#585). The wallet with the person's whole total, or as kept when offline.
 */
export async function keepWallet(wallet: Wallet, profile: string | null): Promise<Wallet> {
  saveWallet(wallet, profile);
  const me = await signedIn();
  if (profile === null || me?.id !== profile) return wallet;
  return syncBeans(profile, me.ownBrowser, api);
}

/**
 * Achievements after a play (ADR 0043, P5-LOOP-03): the play counted into this browser's record
 * for the person (the guest's record joining theirs once, on their own browser), kept, and what it
 * earned just now.
 */
export async function keepFeats(
  play: FeatPlay,
  defs: readonly Achievement[],
  ctx: FeatContext,
  profile: string | null,
  day: string,
): Promise<Feat[]> {
  let feats = loadFeats(profile);
  const me = await signedIn();
  if (profile !== null && me?.id === profile && me.ownBrowser && !feats.guestIn)
    feats = joinFeats(feats, loadFeats(null));
  const { feats: after, earned } = recordFeats(feats, play, defs, ctx, day);
  saveFeats(after, profile);
  return earned;
}
